import { ONBOARDING_PAGE_NS } from '../i18n';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import {
  accountsQuery,
  environmentSettingsQuery,
  FormHeader,
  FormTitle,
  Loader,
  useCurrentUserInfo,
  useCurrentUserStore,
  useDebounceValue,
  useOnboardingStore,
  TestNameResults,
} from '@diana/core';
import { OnboardingTestIds } from '../OnboardingPage';
import { useQuery } from '@tanstack/react-query';
import { useIndustryOptions } from '../useIndustryOptions';

const FormFieldWrapper = ({ label, children }: PropsWithChildren<{ label: string }>) => {
  return (
    <Box>
      <FormHeader>
        <FormTitle title={label} />
      </FormHeader>
      {children}
    </Box>
  );
};

export const BusinessForm = () => {
  const { t } = useTranslation(ONBOARDING_PAGE_NS);
  const { palette } = useTheme();
  const info = useCurrentUserInfo();
  const industries = useIndustryOptions();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const companyName = useOnboardingStore((store) => store.companyName);
  const setCompanyName = useOnboardingStore((store) => store.setCompanyName);
  const ecosystemName = useOnboardingStore((store) => store.ecosystemName) ?? '';
  const setEcosystemName = useOnboardingStore((store) => store.setEcosystemName);
  const industry = useOnboardingStore((store) => store.industry) ?? '';
  const setIndustry = useOnboardingStore((store) => store.setIndustry);
  const websiteUrl = useOnboardingStore((store) => store.websiteUrl);
  const setWebsiteUrl = useOnboardingStore((store) => store.setWebsiteUrl);
  const isError = useOnboardingStore((store) => store.isError);
  const setIsError = useOnboardingStore((store) => store.setIsError);
  const isLoadingRequest = useOnboardingStore((store) => store.isLoadingRequest);
  const setIsLoadingRequest = useOnboardingStore((store) => store.setIsLoadingRequest);
  const generatedEcosystemName = useOnboardingStore((store) => store.generatedEcosystemName);
  const isCompanyOwner = useCurrentUserStore((store) => store.isCompanyOwner());

  const ecosystemNameDebounced = useDebounceValue<string>(ecosystemName, 200);

  const clearError = useCallback(() => {
    setIsError(false);
    setErrorMessage(null);
  }, [setIsError, setErrorMessage]);

  const setError = useCallback(
    (errorMessage: string) => {
      setIsError(true);
      setErrorMessage(errorMessage);
    },
    [setIsError, setErrorMessage],
  );

  const isGenericEmailQuery = accountsQuery.isGenericEmail();
  const { data: isGenericEmail } = useQuery({
    queryKey: isGenericEmailQuery.key,
    queryFn: () => isGenericEmailQuery.fn({ email: info?.email ?? '' }),
    enabled: !!info?.email,
  });

  const domain = info?.email?.split('@')[1] ?? '';

  const validateEcosystemNameQuery = environmentSettingsQuery.testName({
    environmentName: ecosystemNameDebounced,
  });

  useQuery({
    queryKey: validateEcosystemNameQuery.key,
    queryFn: async () => {
      if (!ecosystemNameDebounced || generatedEcosystemName) {
        return clearError();
      }
      if (!/^[\w\d_-]+$/g.test(ecosystemNameDebounced)) {
        return setError(t('error.ecosystem.validation.symbols'));
      }

      setIsLoadingRequest(true);
      const result = await validateEcosystemNameQuery.fn();

      if (!result.allowed && ecosystemNameDebounced !== generatedEcosystemName) {
        setIsLoadingRequest(false);

        switch (result.result) {
          case TestNameResults.Modified:
            return setError(t('error.ecosystem.validation.symbols'));
          case TestNameResults.NameTooShort:
            return setError(t('error.ecosystem.validation.short'));
          case TestNameResults.RepeatedCharError:
            return setError(t('error.ecosystem.validation.repeat'));
          case TestNameResults.EnvironmentExists:
            return setError(t('error.ecosystem.validation.exist'));
          case TestNameResults.EnvironmentReserved:
            return setError(t('error.ecosystem.validation.reserved'));
          case TestNameResults.EnvironmentNameValid:
          default:
            return setError(t('error.ecosystem.validation.invalid'));
        }
      }

      clearError();
      setIsLoadingRequest(false);
      return result;
    },
    retry: false,
  });

  const urlPrefix = useMemo(() => {
    const domain = window.location.hostname;
    const maxLength = 20;
    return domain.length <= maxLength ? `${domain}/` : `${domain.slice(0, maxLength)}\u2026/`;
  }, []);

  useEffect(() => {
    if (info) {
      if (companyName === null) {
        setCompanyName(!isGenericEmail || !isCompanyOwner ? info.companyName : '');
      }
      if (websiteUrl === null) {
        setWebsiteUrl(!isGenericEmail ? domain : '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGenericEmail, info]);

  return (
    <Stack gap={4}>
      <Stack gap={0.5}>
        <Typography variant="h2">{t('steps.business.component.title')}</Typography>
        <Typography color={palette.text.secondary}>
          {t('steps.business.component.description')}
        </Typography>
      </Stack>
      <Stack gap={2}>
        <FormFieldWrapper label={t('steps.business.component.form.company.label')}>
          <TextField
            name="companyName"
            variant="standard"
            fullWidth
            disabled={!isCompanyOwner}
            value={companyName ?? ''}
            onChange={(e) => setCompanyName(e.target.value)}
            inputProps={{ 'data-testid': OnboardingTestIds.companyName }}
            placeholder={t('steps.business.component.form.company.placeholder')}
          />
        </FormFieldWrapper>
        <Stack gap={0.5}>
          <FormFieldWrapper label={t('steps.business.component.form.ecosystem.label')}>
            <TextField
              name="ecosystemName"
              variant="standard"
              disabled={!!generatedEcosystemName}
              helperText={errorMessage || t('steps.business.component.form.ecosystem.hint')}
              error={isError}
              fullWidth
              value={ecosystemName}
              onChange={(e) => setEcosystemName(e.target.value)}
              inputProps={{ 'data-testid': OnboardingTestIds.ecosystemName }}
              placeholder={t('steps.business.component.form.ecosystem.placeholder')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: '-7px', ml: 1 }}>
                    <Typography color={palette.text.placeholder}>{urlPrefix}</Typography>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" sx={{ mr: 1 }}>
                    {isLoadingRequest ? <Loader simple progressProps={{ size: 16 }} /> : null}
                  </InputAdornment>
                ),
              }}
            />
          </FormFieldWrapper>
        </Stack>
        <FormFieldWrapper label={t('steps.business.component.form.industry.label')}>
          <Autocomplete
            fullWidth
            options={industries}
            onChange={(_, value) => setIndustry(value)}
            value={industry}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                name="industry"
                inputProps={{ ...params.inputProps, 'data-testid': OnboardingTestIds.industry }}
                placeholder={t('steps.business.component.form.industry.placeholder')}
              />
            )}
          />
        </FormFieldWrapper>
        <FormFieldWrapper label={t('steps.business.component.form.website.label')}>
          <TextField
            name="websiteUrl"
            variant="standard"
            fullWidth
            value={websiteUrl ?? ''}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            inputProps={{ 'data-testid': OnboardingTestIds.websiteUrl }}
            placeholder={t('steps.business.component.form.website.placeholder')}
          />
        </FormFieldWrapper>
      </Stack>
    </Stack>
  );
};
